import type {
  ContentId,
  GameStateSnapshot,
  GameStateName,
  Mechanic,
  PlayerId,
  ReturnTarget,
  RoomConfig,
  RoomId,
  TensionState,
  VoteChoice,
  VoteOutcome,
  VotingSnapshot,
} from '@juegazo/shared';
import {
  ALL_MECHANICS,
  DEFAULT_BASE_DURATION_MS,
  MAX_BASE_DURATION_MS,
  MAX_BOMBS,
  MAX_PLAYERS,
  MIN_BASE_DURATION_MS,
  MIN_BOMBS,
  MIN_PLAYERS,
} from '@juegazo/shared';
import type { Clock } from './clock';
import type { Rng } from './rng';

export * from './clock';
export * from './rng';

// Los límites de la partida (jugadores, bombas, tiempo base) son reglas
// compartidas: viven en `@juegazo/shared` para que el motor, el servidor y la
// web apliquen exactamente los mismos números.
const RETURN_WINDOW_MS = 5_000;
const EXTRA_DURATION_MS = 15_000;
const BONUS_PER_PASS_MS = 1_000;

interface InternalPlayer {
  id: PlayerId;
  displayName: string;
  totalMs: number;
  explosions: number;
  joinedAt: number;
  turnStartedAt: number | null;
}

interface LastPassSnapshot {
  previousPlayerId: PlayerId;
  bonusAppliedMs: number;
  rotationOrder: PlayerId[];
  rotationIndex: number;
  roundNumber: number;
  lastForcedPassTarget: PlayerId | null;
  roundRotationsLength: number;
}

export interface ContentProvider {
  list(mechanic: Mechanic): ReadonlyArray<{
    id: ContentId;
    mechanic: Mechanic;
    enabled: boolean;
    difficulty: 'easy' | 'medium' | 'hard';
  }>;
  byId(id: ContentId): {
    id: ContentId;
    mechanic: Mechanic;
    enabled: boolean;
    difficulty: 'easy' | 'medium' | 'hard';
  } | null;
}

export interface CreateGameOptions {
  roomId: RoomId;
  totalBombs?: number;
  enabledMechanics?: readonly Mechanic[];
  mixedMode?: boolean;
  baseDurationMs?: number;
  rng: Rng;
  clock: Clock;
  contentProvider?: ContentProvider;
}

/**
 * The saved state of an interrupted game (the local hot-seat mode saves it to
 * the device). `restoreGame` rebuilds the engine from it so the game can be
 * RESUMED instead of only displayed.
 */
export interface RestoreGameOptions {
  roomId: RoomId;
  rng: Rng;
  clock: Clock;
  contentProvider?: ContentProvider;
  totalBombs: number;
  enabledMechanics: readonly Mechanic[];
  mixedMode: boolean;
  baseDurationMs: number;
  initialTimeMs: number;
  players: {
    id: PlayerId;
    displayName: string;
    totalMs: number;
    explosions: number;
    joinedAt?: number;
  }[];
  rotationOrder: PlayerId[];
  rotationIndex: number;
  currentPlayerId: PlayerId | null;
  bombNumber: number;
  currentRound: number;
  remainingTimeMs: number;
  selectedMechanic: Mechanic | null;
  selectedContentId: ContentId | null;
  promptLines: string[];
  tiebreakPlayers?: PlayerId[];
  tiebreakPhase?: boolean;
  lastExplosionPlayerId?: PlayerId | null;
}

export interface GameHandle {
  addPlayer(id: PlayerId, displayName: string): void;
  removePlayer(id: PlayerId): void;
  setContentProvider(provider: ContentProvider): void;
  configure(cfg: Partial<RoomConfig>): void;
  start(): void;
  startBomb(): void;
  passBomb(): void;
  returnBomb(): void;
  pause(): void;
  resume(): void;
  changePrompt(): void;
  explode(): void;
  advanceBetweenBombs(): void;
  earlyFinish(): void;
  startTiebreak(): void;
  startVote(initiatorId: PlayerId): void;
  castVote(playerId: PlayerId, choice: VoteChoice): void;
  decideBombReturn(actorId: PlayerId, target: ReturnTarget): void;
  clearVote(): void;
  tick(): void;
  peek(): { remainingTimeMs: number; state: GameStateName; currentPlayerId: PlayerId | null; tensionState: TensionState; paused: boolean };
  advanceAndTick(): { before: { remainingTimeMs: number; state: GameStateName; currentPlayerId: PlayerId | null; paused: boolean }; after: { remainingTimeMs: number; state: GameStateName; currentPlayerId: PlayerId | null; paused: boolean }; changed: boolean };
  getState(): GameStateSnapshot;
  getHistory(): { roundRotations: PlayerId[][] };
  getScores(): { playerId: PlayerId; displayName: string; totalMs: number; explosions: number }[];
  getFinalResult(): {
    ranking: { playerId: PlayerId; displayName: string; totalMs: number; explosions: number }[];
    winnerId: PlayerId | null;
    exactTie: boolean;
    tiedPlayerIds: PlayerId[];
    finished: boolean;
  };
}

interface CreateGameResult extends GameHandle {}

const shuffle = <T>(arr: T[], rng: Rng): T[] => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const buildPromptLines = (
  mechanic: Mechanic | null,
  content: { id: ContentId; mechanic: Mechanic; [k: string]: unknown } | null,
  lastChainWord: string | null,
): string[] => {
  if (!mechanic || !content) return [];
  const lines: string[] = [];
  switch (mechanic) {
    case 'category':
      lines.push(String(content['category'] ?? '').toUpperCase());
      break;
    case 'starts_with_letter':
      // lines.push(`LETRA ${String(content['letter'] ?? '').toUpperCase()}`);
      lines.push(`${String(content['letter'] ?? '').toUpperCase()}`);
      break;
    case 'contains':
      lines.push(`"${String(content['sequence'] ?? '').toUpperCase()}"`);
      break;
    case 'excludes_letter':
      lines.push(String(content['category'] ?? '').toUpperCase());
      // lines.push(`LETRA ${String(content['letter'] ?? '').toUpperCase()}`);
      lines.push(`${String(content['letter'] ?? '').toUpperCase()}`);
      break;
    case 'chain':
      lines.push((lastChainWord ?? String(content['startWord'] ?? '')).toUpperCase());
      break;
    case 'dual_condition':
      lines.push(String(content['category'] ?? '').toUpperCase());
      // lines.push(`LETRA ${String(content['letter'] ?? '').toUpperCase()}`);
      lines.push(`${String(content['letter'] ?? '').toUpperCase()}`);
      break;
    case 'name_three':
      lines.push(`3 ${String(content['category'] ?? '').toUpperCase()}`);
      break;
  }
  return lines;
};

export const createGame = (opts: CreateGameOptions): CreateGameResult =>
  createGameInternal(opts);

const createGameInternal = (
  opts: CreateGameOptions,
  restore?: RestoreGameOptions,
): CreateGameResult => {
  let totalBombs = opts.totalBombs ?? 5;
  if (totalBombs < MIN_BOMBS || totalBombs > MAX_BOMBS) {
    throw new Error(`Invalid totalBombs: ${totalBombs}`);
  }
  const enabledMechanics = (opts.enabledMechanics ?? [...ALL_MECHANICS]).slice();
  if (enabledMechanics.length === 0) {
    throw new Error('At least one mechanic must be enabled');
  }
  let mixedMode = opts.mixedMode ?? false;
  let baseDurationMs = opts.baseDurationMs ?? DEFAULT_BASE_DURATION_MS;
  if (baseDurationMs < MIN_BASE_DURATION_MS || baseDurationMs > MAX_BASE_DURATION_MS) {
    throw new Error(`Invalid baseDurationMs: ${baseDurationMs}`);
  }
  const rng = opts.rng;
  const clock = opts.clock;

  const players = new Map<PlayerId, InternalPlayer>();
  let contentProvider: ContentProvider | null = opts.contentProvider ?? null;

  let state: GameStateName = 'lobby';
  let bombNumber = 0;
  let currentRound = 1;
  let currentPlayerId: PlayerId | null = null;
  let rotationOrder: PlayerId[] = [];
  let rotationIndex = 0;
  let initialTimeMs = 0;
  let bombEndsAt: number | null = null;
  let tensionState: TensionState = 'normal';
  let selectedMechanic: Mechanic | null = null;
  let selectedContentId: ContentId | null = null;
  let promptLines: string[] = [];
  let paused = false;
  let lastPassSnapshot: LastPassSnapshot | null = null;
  let returnWindowExpiresAt: number | null = null;
  let voting: VotingSnapshot | null = null;
  let lastForcedPassTarget: PlayerId | null = null;
  let lastExplosionPlayerId: PlayerId | null = null;
  let tiebreakPlayers: PlayerId[] = [];
  // GAME-TIE-004/005: the tiebreak is resolved by elimination, never by time.
  // `tiebreakPhase` marks the whole tiebreak round, `tiebreakTiedPlayers`
  // keeps the players that were tied when it started (for reporting) and
  // `tiebreakWinnerId` is the last survivor (the definitive winner).
  let tiebreakPhase = false;
  let tiebreakTiedPlayers: PlayerId[] = [];
  let tiebreakWinnerId: PlayerId | null = null;
  // The state a paused round came from, so a paused tiebreak resumes into the
  // tiebreak and not into a plain round.
  let pausedFromState: GameStateName = 'playing';
  let lastChainWord: string | null = null;
  let usedContentIds = new Set<ContentId>();
  const roundRotations: PlayerId[][] = [];

  let tensionThresholds = { acelerado: 0, inminente: 0 };

  // Los umbrales de tensión dependen del tiempo inicial de la bomba. Se
  // calculan SIEMPRE igual (también al reanudar una partida), así la tensión
  // de una partida reanudada no se pierde: el motor no persiste nada nuevo en
  // el snapshot, los recalcula con el `initialTimeMs` restaurado.
  const computeTensionThresholds = (initial: number) => {
    const pAc = 0.45 + rng.next() * 0.1;
    const pIn = 0.15 + rng.next() * 0.1;
    return {
      acelerado: Math.floor(initial * pAc),
      inminente: Math.floor(initial * pIn),
    };
  };

  let effectiveTime = 0;
  let lastTickWallTime = clock.now();

  if (restore) {
    // S2 (engine side): rebuild an interrupted game. The scores, the rotation
    // and the holder survive the resume, and the bomb continues with the time
    // that was left: the closed period is NOT deducted from anybody.
    if (restore.players.length < MIN_PLAYERS || restore.players.length > MAX_PLAYERS) {
      throw new Error(`Players must be between ${MIN_PLAYERS} and ${MAX_PLAYERS}`);
    }
    if (restore.rotationIndex < 0 || restore.rotationIndex >= Math.max(1, restore.rotationOrder.length)) {
      throw new Error(`Invalid rotationIndex: ${restore.rotationIndex}`);
    }
    for (const p of restore.players) {
      players.set(p.id, {
        id: p.id,
        displayName: p.displayName,
        totalMs: p.totalMs,
        explosions: p.explosions,
        joinedAt: p.joinedAt ?? clock.now(),
        turnStartedAt: null,
      });
    }
    state = restore.tiebreakPhase ? 'tiebreak_playing' : 'playing';
    bombNumber = restore.bombNumber;
    currentRound = restore.currentRound;
    currentPlayerId = restore.currentPlayerId;
    rotationOrder = restore.rotationOrder.slice();
    rotationIndex = restore.rotationIndex;
    initialTimeMs = restore.initialTimeMs;
    tensionThresholds = computeTensionThresholds(initialTimeMs);
    selectedMechanic = restore.selectedMechanic;
    selectedContentId = restore.selectedContentId;
    promptLines = restore.promptLines.slice();
    tiebreakPhase = restore.tiebreakPhase ?? false;
    tiebreakPlayers = (restore.tiebreakPlayers ?? []).slice();
    tiebreakTiedPlayers = tiebreakPlayers.slice();
    lastExplosionPlayerId = restore.lastExplosionPlayerId ?? null;
    paused = false;
    pausedFromState = state;
    // The bomb continues with the time that was left and the clock starts
    // counting NOW, so nobody is credited for the closed period.
    bombEndsAt = effectiveTime + Math.max(0, restore.remainingTimeMs);
    if (currentPlayerId) {
      const active = players.get(currentPlayerId);
      if (active) active.turnStartedAt = effectiveTime;
    }
  }

  const computeRemainingMs = (): number => {
    if (bombEndsAt === null) return 0;
    return Math.max(0, bombEndsAt - effectiveTime);
  };

  const recomputeTension = (): TensionState => {
    const remaining = computeRemainingMs();
    if (remaining < tensionThresholds.inminente) return 'inminente';
    if (remaining <= tensionThresholds.acelerado) return 'acelerado';
    return 'normal';
  };

  const updateTensionMonotonic = (next: TensionState) => {
    const order: TensionState[] = ['normal', 'acelerado', 'inminente'];
    if (order.indexOf(next) > order.indexOf(tensionState)) {
      tensionState = next;
    }
  };

  const generateRotation = (excludeSet: ReadonlySet<PlayerId>): PlayerId[] => {
    const baseIds = Array.from(players.keys()).filter((id) =>
      tiebreakPlayers.length === 0 ? true : tiebreakPlayers.includes(id),
    );
    if (tiebreakPlayers.length > 0) {
      return shuffle(baseIds, rng);
    }
    const excluded = baseIds.filter((id) => excludeSet.has(id));
    const rest = baseIds.filter((id) => !excludeSet.has(id));
    if (excluded.length > 0 && rest.length >= 1) {
      return [...shuffle(rest, rng), ...shuffle(excluded, rng)];
    }
    return shuffle(baseIds, rng);
  };

  const selectMechanic = (): Mechanic => {
    if (!mixedMode || enabledMechanics.length === 1) {
      return enabledMechanics[0];
    }
    const candidates = enabledMechanics.filter((m) => m !== selectedMechanic);
    const pool = candidates.length > 0 ? candidates : enabledMechanics;
    const idx = Math.floor(rng.next() * pool.length);
    return pool[Math.min(idx, pool.length - 1)];
  };

  const selectContent = (
    mechanic: Mechanic,
  ): { id: ContentId; mechanic: Mechanic; [k: string]: unknown } | null => {
    if (!contentProvider) return null;
    const all = contentProvider.list(mechanic).filter((i) => i.enabled);
    const unused = all.filter((i) => !usedContentIds.has(i.id));
    const pool = unused.length > 0 ? unused : all;
    if (pool.length === 0) return null;
    const idx = Math.floor(rng.next() * pool.length);
    return pool[Math.min(idx, pool.length - 1)] as { id: ContentId; mechanic: Mechanic; [k: string]: unknown };
  };

  const computeRanking = () => {
    const entries = Array.from(players.values()).map((p) => ({
      playerId: p.id,
      displayName: p.displayName,
      totalMs: p.totalMs,
      explosions: p.explosions,
    }));
    return entries.sort(
      (a, b) =>
        a.explosions - b.explosions ||
        a.totalMs - b.totalMs ||
        a.displayName.localeCompare(b.displayName),
    );
  };

  const detectTie = (ranking: ReturnType<typeof computeRanking>) => {
    if (ranking.length === 0) return { exactTie: false, tied: [] as PlayerId[] };
    const top = ranking[0];
    const tied = ranking
      .filter((r) => r.explosions === top.explosions && r.totalMs === top.totalMs)
      .map((r) => r.playerId);
    return { exactTie: tied.length > 1, tied };
  };

  // S8: a pending tiebreak must be published with the SAME predicate that
  // `advanceBetweenBombs` applies when the round ends, so the client sees
  // exactly what the server will do next.
  const computePendingTiebreak = (): boolean => {
    if (state === 'tiebreak_ready') return true;
    if (state === 'exploded' && !tiebreakPhase && bombNumber >= totalBombs) {
      return detectTie(computeRanking()).exactTie;
    }
    return false;
  };

  const buildSnapshot = (): GameStateSnapshot => {
    const playerSnapshots = Array.from(players.values())
      .sort((a, b) => a.joinedAt - b.joinedAt)
      .map((p) => ({
        id: p.id,
        displayName: p.displayName,
        isAdmin: false,
        isConnected: true,
        joinedAt: p.joinedAt,
      }));
    return {
      roomId: opts.roomId,
      state,
      bombNumber,
      totalBombs,
      currentRound,
      currentPlayerId,
      rotationOrder: rotationOrder.slice(),
      rotationIndex,
      lastForcedPassTarget,
      remainingTimeMs: computeRemainingMs(),
      initialTimeMs,
      tensionState,
      selectedMechanic,
      selectedContentId,
      promptLines: promptLines.slice(),
      paused,
      pausedBy: null,
      countdownEndsAt: null,
      bombEndsAt,
      returnWindowExpiresAt,
      returnWindowMsRemaining:
        returnWindowExpiresAt === null ? 0 : Math.max(0, returnWindowExpiresAt - effectiveTime),
      voting: voting
        ? {
            initiatorId: voting.initiatorId,
            votes: { ...voting.votes },
            phase: voting.phase,
            outcome: voting.outcome,
            returnDecision: voting.returnDecision ? { ...voting.returnDecision } : null,
          }
        : null,
      lastExplosionPlayerId,
      tiebreakPlayers: tiebreakPlayers.slice(),
      // S7: the clients must not guess whether the current round is a
      // tiebreak round.
      tiebreakPhase,
      // S8: whether the next screen is a tiebreak instead of the results.
      pendingTiebreak: computePendingTiebreak(),
      winnerId: null,
      players: playerSnapshots,
      enabledMechanics: enabledMechanics.slice(),
      mixedMode,
      baseDurationMs,
    };
  };

  const startBombInternal = () => {
    // The bomb window starts NOW. The engine can sit idle between games (the
    // server does not tick a room that is not in game), so the wall-clock gap
    // since the last tick MUST NOT be charged to the new bomb: it would
    // detonate it instantly. `tickTime` folds the gap into the logical clock
    // and re-anchors the wall clock, so the countdown starts from this moment.
    tickTime();
    bombNumber++;
    currentRound = 1;
    selectedMechanic = selectMechanic();
    const content = selectContent(selectedMechanic);
    selectedContentId = content?.id ?? null;
    if (selectedContentId) usedContentIds.add(selectedContentId);
    promptLines = buildPromptLines(selectedMechanic, content, lastChainWord);
    if (content && content['startWord']) {
      lastChainWord = String(content['startWord']);
    }

    const r0 = rng.next();
    const initial = baseDurationMs + Math.round(r0 * EXTRA_DURATION_MS);
    initialTimeMs = initial;
    bombEndsAt = effectiveTime + initial;

    tensionThresholds = computeTensionThresholds(initial);
    tensionState = 'normal';

    rotationOrder = generateRotation(new Set());
    rotationIndex = 0;
    currentPlayerId = rotationOrder[0] ?? null;
    roundRotations.push(rotationOrder.slice());

    const active = currentPlayerId ? players.get(currentPlayerId) : null;
    if (active) active.turnStartedAt = effectiveTime;

    lastPassSnapshot = null;
    returnWindowExpiresAt = null;
    state = 'playing';
  };

  const advanceToNextPlayer = () => {
    if (currentPlayerId) {
      const active = players.get(currentPlayerId);
      if (active) active.turnStartedAt = null;
    }

    while (true) {
      rotationIndex++;
      if (rotationIndex >= rotationOrder.length) {
        currentRound++;
        const last = currentPlayerId;
        const excludeSet = new Set<PlayerId>();
        if (last) excludeSet.add(last);
        if (lastForcedPassTarget) excludeSet.add(lastForcedPassTarget);
        rotationOrder = generateRotation(excludeSet);
        rotationIndex = 0;
        roundRotations.push(rotationOrder.slice());
        lastForcedPassTarget = null; // reset for new round
      }
      const candidate = rotationOrder[rotationIndex];
      if (candidate && candidate === lastForcedPassTarget) {
        continue; // skip: X already had the bomb this round via forced pass
      }
      currentPlayerId = candidate ?? null;
      break;
    }

    if (currentPlayerId) {
      const active = players.get(currentPlayerId);
      if (active) active.turnStartedAt = effectiveTime;
    }
  };

  const tickTime = () => {
    const wallNow = clock.now();
    if (!paused) {
      const delta = Math.max(0, wallNow - lastTickWallTime);
      effectiveTime += delta;
    }
    lastTickWallTime = wallNow;
  };

  const checkExplosion = () => {
    if (state !== 'playing' && state !== 'tiebreak_playing') return;
    if (computeRemainingMs() <= 0) {
      doExplode();
    }
  };

  const doExplode = () => {
    const wasTiebreak = state === 'tiebreak_playing';
    if (currentPlayerId) {
      const player = players.get(currentPlayerId);
      if (player) {
        player.explosions++;
        player.turnStartedAt = null;
      }
      lastExplosionPlayerId = currentPlayerId;
      // GAME-TIE-004: the player whose bomb explodes during the tiebreak is
      // eliminated from it. The last survivor is the winner (GAME-TIE-005).
      if (wasTiebreak) {
        tiebreakPlayers = tiebreakPlayers.filter((p) => p !== currentPlayerId);
      }
    }
    state = 'exploded';
  };

  const resolveVote = () => {
    if (!voting) return;
    let cVal = 0;
    let cNoVal = 0;
    for (const choice of Object.values(voting.votes)) {
      if (choice === 'valida') cVal++;
      else cNoVal++;
    }
    let outcome: VoteOutcome;
    if (cVal > cNoVal) outcome = 'valida';
    else if (cNoVal > cVal) outcome = 'no_valida';
    else {
      // tie → rng decides
      outcome = rng.next() < 0.5 ? 'valida' : 'no_valida';
    }
    voting.phase = 'decided';
    voting.outcome = outcome;
  };

  const applyForcedPass = (target: PlayerId) => {
    const previousPlayerId = currentPlayerId;
    const previousPlayer = previousPlayerId ? players.get(previousPlayerId) : null;
    const bonus = BONUS_PER_PASS_MS;
    if (bombEndsAt !== null) bombEndsAt += bonus;
    if (previousPlayer) previousPlayer.turnStartedAt = null;
    currentPlayerId = target;
    const newActive = players.get(target);
    if (newActive) newActive.turnStartedAt = effectiveTime;
    returnWindowExpiresAt = effectiveTime + RETURN_WINDOW_MS;
    lastPassSnapshot = {
      previousPlayerId: previousPlayerId ?? target,
      bonusAppliedMs: bonus,
      rotationOrder: rotationOrder.slice(),
      rotationIndex,
      roundNumber: currentRound,
      // El snapshot guarda el valor ANTERIOR de `lastForcedPassTarget` (igual
      // que `passBomb`): si el pase forzado se deshace con `returnBomb`, el
      // jugador forzado no queda marcado como "ya tuvo la bomba en la ronda" y
      // no pierde su turno por un devolver legal.
      lastForcedPassTarget,
      roundRotationsLength: roundRotations.length,
    };
    lastForcedPassTarget = target;
  };

  const handle: GameHandle = {
    addPlayer(id, displayName) {
      if (players.has(id)) return;
      players.set(id, {
        id,
        displayName,
        totalMs: 0,
        explosions: 0,
        joinedAt: clock.now(),
        turnStartedAt: null,
      });
    },
    removePlayer(id) {
      // Audit critical #2 (S3/S6): removing the current holder must hand the
      // bomb to the next live rotation slot, otherwise every remaining pass
      // is rejected ('No autorizado') and the explosion is credited to a
      // ghost id that no longer exists in `players`.
      const wasHolder = currentPlayerId === id;
      const removedIdx = rotationOrder.indexOf(id);
      players.delete(id);
      rotationOrder = rotationOrder.filter((p) => p !== id);
      if (tiebreakPlayers.length > 0) {
        tiebreakPlayers = tiebreakPlayers.filter((p) => p !== id);
      }
      if (lastForcedPassTarget === id) {
        lastForcedPassTarget = null;
      }
      // Un undo pendiente no puede resucitar al jugador que se va, así que se
      // cancela. El flujo CONTINÚA: el relevo del titular y la resolución del
      // desempate (GAME-TIE-005) tienen que correr igual.
      const cancelledUndo = lastPassSnapshot?.previousPlayerId === id;
      if (cancelledUndo) {
        lastPassSnapshot = null;
        returnWindowExpiresAt = null;
      }
      const liveTurn = state === 'playing' || state === 'paused' || state === 'tiebreak_playing';
      if (wasHolder && liveTurn) {
        if (rotationOrder.length === 0) {
          currentPlayerId = null;
        } else {
          if (removedIdx !== -1 && removedIdx === rotationIndex) {
            // The array shifted under the holder; step back so the advance
            // lands exactly on the player that followed them.
            rotationIndex--;
          }
          advanceToNextPlayer();
        }
      } else if (!wasHolder && removedIdx !== -1 && removedIdx < rotationIndex) {
        rotationIndex--;
      }
      // GAME-TIE-005: if the tiebreak lost its last competitor, the survivor
      // is the definitive winner. This also covers a tiebreak that was paused
      // when the competitor was removed.
      if (
        tiebreakPhase &&
        tiebreakPlayers.length <= 1 &&
        (state === 'tiebreak_playing' || state === 'paused')
      ) {
        tiebreakWinnerId = tiebreakPlayers[0] ?? null;
        paused = false;
        state = 'finished';
      }
    },
    setContentProvider(provider) {
      contentProvider = provider;
    },
    configure(cfg) {
      if (cfg.totalBombs !== undefined) {
        if (cfg.totalBombs < MIN_BOMBS || cfg.totalBombs > MAX_BOMBS) throw new Error('Invalid totalBombs');
        totalBombs = cfg.totalBombs;
      }
      if (cfg.enabledMechanics !== undefined) {
        if (cfg.enabledMechanics.length === 0) throw new Error('At least one mechanic required');
        enabledMechanics.length = 0;
        (cfg.enabledMechanics as Mechanic[]).forEach((m) => enabledMechanics.push(m));
      }
      if (cfg.mixedMode !== undefined) {
        mixedMode = cfg.mixedMode;
      }
      if (cfg.baseDurationMs !== undefined) {
        if (cfg.baseDurationMs < MIN_BASE_DURATION_MS || cfg.baseDurationMs > MAX_BASE_DURATION_MS) {
          throw new Error('Invalid baseDurationMs');
        }
        baseDurationMs = cfg.baseDurationMs;
      }
    },
    start() {
      if (players.size < MIN_PLAYERS || players.size > MAX_PLAYERS) {
        throw new Error(`Players must be between ${MIN_PLAYERS} and ${MAX_PLAYERS}`);
      }
      if (enabledMechanics.length === 0) throw new Error('At least one mechanic required');
      state = 'lobby';
      bombNumber = 0;
      lastExplosionPlayerId = null;
      tiebreakPlayers = [];
      tiebreakPhase = false;
      tiebreakTiedPlayers = [];
      tiebreakWinnerId = null;
      pausedFromState = 'playing';
      usedContentIds = new Set();
      // S10: a replay is a brand-new game, so the runtime state of the
      // previous one MUST NOT survive it. A stale pause flag keeps the clock
      // frozen, an open vote blocks every pass ('Cannot pass bomb while vote
      // is open') and the accumulated scores would be carried over.
      paused = false;
      voting = null;
      lastPassSnapshot = null;
      returnWindowExpiresAt = null;
      lastForcedPassTarget = null;
      lastChainWord = null;
      currentPlayerId = null;
      bombEndsAt = null;
      initialTimeMs = 0;
      selectedContentId = null;
      promptLines = [];
      tensionState = 'normal';
      roundRotations.length = 0;
      for (const player of players.values()) {
        player.totalMs = 0;
        player.explosions = 0;
        player.turnStartedAt = null;
      }
    },
    startBomb() {
      if (players.size < MIN_PLAYERS) throw new Error('Not enough players');
      startBombInternal();
    },
    passBomb() {
      tickTime();
      if (voting !== null) throw new Error('Cannot pass bomb while vote is open');
      if (state !== 'playing' && state !== 'tiebreak_playing') {
        throw new Error('Cannot pass bomb in current state');
      }
      if (currentPlayerId === null) throw new Error('No active player');
      if (computeRemainingMs() <= 0) throw new Error('Bomb already exploded');
      const player = players.get(currentPlayerId);
      if (!player) throw new Error('Player not found');
      if (player.turnStartedAt === null) player.turnStartedAt = effectiveTime;
      const duration = effectiveTime - player.turnStartedAt;
      // GAME-TIE-004: during the tiebreak the times are NOT compared, so the
      // tiebreak round must not pollute the measured times of the main game.
      if (!tiebreakPhase) player.totalMs += duration;

      const bonus = BONUS_PER_PASS_MS;
      if (bombEndsAt !== null) bombEndsAt += bonus;

      lastPassSnapshot = {
        previousPlayerId: currentPlayerId,
        bonusAppliedMs: bonus,
        rotationOrder: rotationOrder.slice(),
        rotationIndex,
        roundNumber: currentRound,
        lastForcedPassTarget,
        roundRotationsLength: roundRotations.length,
      };
      returnWindowExpiresAt = effectiveTime + RETURN_WINDOW_MS;

      advanceToNextPlayer();
    },
    returnBomb() {
      tickTime();
      if (voting !== null) throw new Error('Cannot return bomb while vote is open');
      if (state !== 'playing' && state !== 'tiebreak_playing') {
        throw new Error('Cannot return bomb in current state');
      }
      if (!lastPassSnapshot) throw new Error('No pass to undo');
      if (returnWindowExpiresAt === null || effectiveTime >= returnWindowExpiresAt) {
        throw new Error('Return window expired');
      }
      const snap = lastPassSnapshot;
      // Invariante: `removePlayer` cancela el undo pendiente cuando el
      // jugador que pasó la bomba se va, así que el `previousPlayerId` del
      // snapshot sigue vivo acá. Si algún día deja de cumplirse, el undo se
      // cancela (la bomba queda con el holder actual) en vez de lanzar un
      // error genérico: nunca se resucita a un jugador que ya no está.
      const previous = players.get(snap.previousPlayerId);
      const restoredOrder = snap.rotationOrder.filter((p) => players.has(p));
      const restoredIndex = restoredOrder.indexOf(snap.previousPlayerId);
      if (!previous || restoredIndex === -1) {
        lastPassSnapshot = null;
        returnWindowExpiresAt = null;
        return;
      }
      if (currentPlayerId) {
        const current = players.get(currentPlayerId);
        if (current) current.turnStartedAt = null;
      }
      if (bombEndsAt !== null) bombEndsAt -= snap.bonusAppliedMs;
      // The snapshot was captured at pass time: a player that left the room
      // while the return window was open must NOT be resurrected by the undo,
      // and the restored index must point at the player that holds the bomb
      // again (the rotation may have shrunk while the window was open).
      currentPlayerId = snap.previousPlayerId;
      rotationOrder = restoredOrder;
      rotationIndex = restoredIndex;
      currentRound = snap.roundNumber;
      lastForcedPassTarget =
        snap.lastForcedPassTarget && players.has(snap.lastForcedPassTarget)
          ? snap.lastForcedPassTarget
          : null;
      roundRotations.length = snap.roundRotationsLength;
      previous.turnStartedAt = effectiveTime;
      lastPassSnapshot = null;
      returnWindowExpiresAt = null;
      updateTensionMonotonic(recomputeTension());
    },
    pause() {
      tickTime();
      if (state !== 'playing' && state !== 'tiebreak_playing') {
        throw new Error('Can only pause while playing');
      }
      pausedFromState = state;
      paused = true;
      state = 'paused';
    },
    resume() {
      if (voting !== null) throw new Error('Resolve the vote before resuming');
      if (state !== 'paused') throw new Error('Can only resume from paused');
      paused = false;
      // A paused tiebreak resumes into the tiebreak, not into a plain round.
      state = pausedFromState === 'tiebreak_playing' ? 'tiebreak_playing' : 'playing';
    },
    changePrompt() {
      if (!paused) throw new Error('Can only change prompt while paused');
      if (!selectedMechanic) throw new Error('No active prompt');
      const content = selectContent(selectedMechanic);
      selectedContentId = content?.id ?? null;
      if (selectedContentId) usedContentIds.add(selectedContentId);
      // La palabra de la mecánica `chain` se refresca desde el contenido NUEVO
      // (igual que en `startBombInternal`) antes de construir el prompt: si no,
      // la consigna seguiría mostrando la palabra vieja mientras
      // `selectedContentId` apunta al contenido nuevo.
      if (content && content['startWord']) {
        lastChainWord = String(content['startWord']);
      }
      promptLines = buildPromptLines(selectedMechanic, content, lastChainWord);
    },
    explode() {
      tickTime();
      doExplode();
    },
    advanceBetweenBombs() {
      if (state !== 'exploded') throw new Error('Not in exploded state');
      if (tiebreakPhase) {
        // GAME-TIE-004/005: the exploded player was already eliminated in
        // doExplode. With one survivor left the tiebreak is resolved (that
        // survivor is the definitive winner); otherwise another tiebreak bomb
        // is played among the survivors.
        if (tiebreakPlayers.length <= 1) {
          tiebreakWinnerId = tiebreakPlayers[0] ?? null;
          state = 'finished';
          return;
        }
        startBombInternal();
        state = 'tiebreak_playing';
        return;
      }
      if (bombNumber >= totalBombs) {
        const ranking = computeRanking();
        const { exactTie, tied } = detectTie(ranking);
        if (exactTie) {
          tiebreakPlayers = tied;
          state = 'tiebreak_ready';
          // A pending tiebreak is NOT a finished game.
        } else {
          state = 'finished';
        }
        return;
      }
      state = 'between_bombs';
    },
    earlyFinish() {
      // La partida termina sí o sí: el ranking lo calcula `getState()` al
      // publicar el resultado.
      state = 'finished';
    },
    startTiebreak() {
      if (state !== 'tiebreak_ready' && state !== 'finished') {
        throw new Error('Not in tiebreak-ready state');
      }
      const ranks = computeRanking();
      const top = ranks[0];
      tiebreakPlayers = top
        ? ranks
            .filter((r) => r.explosions === top.explosions && r.totalMs === top.totalMs)
            .map((r) => r.playerId)
        : [];
      if (tiebreakPlayers.length < 2) throw new Error('No tiebreak needed');
      tiebreakPhase = true;
      tiebreakWinnerId = null;
      tiebreakTiedPlayers = tiebreakPlayers.slice();
      lastChainWord = null;
      usedContentIds = new Set();
      startBombInternal();
      state = 'tiebreak_playing';
    },
    startVote(initiatorId) {
      tickTime();
      if (state !== 'paused') throw new Error('Can only start vote while paused');
      if (voting !== null) throw new Error('Vote already in progress');
      if (!lastPassSnapshot) throw new Error('No pass to contest');
      voting = {
        initiatorId,
        votes: {},
        phase: 'casting',
        outcome: null,
        returnDecision: null,
      };
    },
    castVote(playerId, choice) {
      tickTime();
      if (!voting) throw new Error('No vote in progress');
      if (voting.phase !== 'casting') throw new Error('Vote already resolved');
      if (!players.has(playerId)) throw new Error('Player not in game');
      voting.votes[playerId] = choice;
      // Mayoría absoluta sobre TODOS los participantes, contando solo una
      // opción. Con 5 jugadores se necesitan 3 VÁLIDOS (o 3 INVALIDOS) para
      // decidir; un reparto 2-1 (o similar) NO resuelve todavía.
      const totalPlayers = players.size;
      const majority = Math.floor(totalPlayers / 2) + 1;
      let cVal = 0;
      let cNoVal = 0;
      for (const v of Object.values(voting.votes)) {
        if (v === 'valida') cVal++;
        else cNoVal++;
      }
      // Resuelve cuando una opción alcanza la mayoría absoluta (sobre todos
      // los participantes, p.ej. 3 de 5). También resuelve si TODOS ya votaron
      // pero ninguna opción llegó a la mayoría absoluta (p.ej. empate 2-2 con
      // 4 jugadores), para que la votación nunca quede colgada.
      const everyoneVoted = Object.keys(voting.votes).length >= totalPlayers;
      if (cVal >= majority || cNoVal >= majority || everyoneVoted) {
        resolveVote();
      }
    },
    decideBombReturn(actorId, target) {
      tickTime();
      if (!voting) throw new Error('No vote in progress');
      if (voting.phase !== 'decided') throw new Error('Vote not yet resolved');
      if (voting.outcome !== 'no_valida') throw new Error('Vote did not resolve to no_valida');
      if (voting.returnDecision !== null) throw new Error('Decision already made');
      if (typeof target !== 'string') {
        throw new Error('Target must be a PlayerId or "no_return"');
      }
      if (target !== 'no_return') {
        if (!players.has(target)) throw new Error('Target player not in game');
        if (target === currentPlayerId) throw new Error('Cannot return to active player');
        applyForcedPass(target);
      }
      voting.returnDecision = { actorId, target };
    },
    clearVote() {
      tickTime();
      if (!voting) throw new Error('No vote in progress');
      if (voting.phase !== 'decided') throw new Error('Vote not yet resolved');
      voting = null;
    },
    tick() {
      tickTime();
      if (state === 'playing' || state === 'tiebreak_playing') {
        updateTensionMonotonic(recomputeTension());
      }
      checkExplosion();
    },
    peek() {
      tickTime();
      return {
        remainingTimeMs: computeRemainingMs(),
        state,
        currentPlayerId,
        tensionState,
        paused,
      };
    },
    advanceAndTick() {
      const beforeRemaining = computeRemainingMs();
      const beforeState = state;
      const beforePlayer = currentPlayerId;
      const beforePaused = paused;
      tickTime();
      if (state === 'playing' || state === 'tiebreak_playing') {
        updateTensionMonotonic(recomputeTension());
      }
      checkExplosion();
      const afterRemaining = computeRemainingMs();
      const changed =
        beforeRemaining !== afterRemaining ||
        beforeState !== state ||
        beforePlayer !== currentPlayerId ||
        beforePaused !== paused;
      return {
        before: { remainingTimeMs: beforeRemaining, state: beforeState, currentPlayerId: beforePlayer, paused: beforePaused },
        after: { remainingTimeMs: afterRemaining, state, currentPlayerId, paused },
        changed,
      };
    },
    getState() {
      tickTime();
      if (state === 'playing' || state === 'tiebreak_playing') {
        updateTensionMonotonic(recomputeTension());
      }
      checkExplosion();
      const snap = buildSnapshot();
      if (state === 'finished') {
        if (tiebreakPhase && tiebreakWinnerId !== null) {
          // The tiebreak decided the winner by elimination, not by time.
          snap.winnerId = tiebreakWinnerId;
          snap.tiebreakPlayers = tiebreakTiedPlayers.slice();
        } else {
          const ranking = computeRanking();
          const { exactTie, tied } = detectTie(ranking);
          if (exactTie) {
            snap.tiebreakPlayers = tied.slice();
          } else {
            snap.winnerId = ranking[0]?.playerId ?? null;
          }
        }
      }
      return snap;
    },
    getHistory() {
      return { roundRotations: roundRotations.slice() };
    },
    getScores() {
      return Array.from(players.values()).map((p) => ({
        playerId: p.id,
        displayName: p.displayName,
        totalMs: p.totalMs,
        explosions: p.explosions,
      }));
    },
    getFinalResult() {
      const ranking = computeRanking();
      const { exactTie, tied } = detectTie(ranking);
      const resolvedTiebreak = tiebreakPhase && tiebreakWinnerId !== null;
      const winnerId = resolvedTiebreak
        ? tiebreakWinnerId
        : state === 'finished' && !exactTie
          ? ranking[0]?.playerId ?? null
          : null;
      return {
        ranking,
        winnerId,
        exactTie: resolvedTiebreak ? false : exactTie,
        tiedPlayerIds: tiebreakPhase ? tiebreakTiedPlayers.slice() : tied,
        finished: state === 'finished',
      };
    },
  };

  return handle;
};

/**
 * Rebuilds the engine from a saved (interrupted) game so the local mode can
 * RESUME it. It reuses the very same internals as `createGame`.
 */
export const restoreGame = (opts: RestoreGameOptions): GameHandle =>
  createGameInternal(
    {
      roomId: opts.roomId,
      totalBombs: opts.totalBombs,
      enabledMechanics: opts.enabledMechanics,
      mixedMode: opts.mixedMode,
      baseDurationMs: opts.baseDurationMs,
      rng: opts.rng,
      clock: opts.clock,
      contentProvider: opts.contentProvider,
    },
    opts,
  );


